/**
 * SearchModal Component
 * Full-screen search modal with predictive results
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { predictiveSearch } from '~/lib/shopify/api';
import { formatPrice, getProductUrl, getCollectionUrl } from '~/lib/shopify/api';
import { useDebouncedValue } from '~/hooks/useDebouncedValue';
import { trackSearch } from '~/lib/tracking/gtm';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchResults {
  products: Array<{
    id: string;
    handle: string;
    title: string;
    vendor: string;
    featuredImage: { url: string; altText: string | null } | null;
    priceRange: { minVariantPrice: { amount: string; currencyCode: string } };
  }>;
  collections: Array<{
    id: string;
    handle: string;
    title: string;
    image: { url: string; altText: string | null } | null;
  }>;
}

const popularSearches = [
  'Jabra Evolve2',
  'Wireless Bluetooth',
  'USB-C Headset',
  'Noise Cancelling',
  'Teams Certified',
  'Poly Voyager',
];

export function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebouncedValue(query, 300);

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
      setResults(null);
    }
  }, [isOpen]);

  // Handle search
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setResults(null);
      return;
    }

    let cancelled = false;

    const search = async () => {
      setIsLoading(true);
      try {
        const data = await predictiveSearch(debouncedQuery, 6);
        if (!cancelled) {
          setResults(data);
        }
      } catch (error) {
        console.error('Search error:', error);
        if (!cancelled) {
          // Return empty results on error so user sees "no results" message
          setResults({ products: [], collections: [] });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    search();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      // Open search with Cmd/Ctrl + K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      trackSearch(query.trim());
      window.location.href = `/search?q=${encodeURIComponent(query.trim())}`;
      onClose();
    }
  };

  const handleQuickSearch = (term: string) => {
    setQuery(term);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-surface-black/95 backdrop-blur-xl z-50 overflow-y-auto"
        >
          <div className="container-narrow py-8">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-accent-silver hover:text-accent-white hover:bg-surface-slate rounded-xl transition-colors"
              aria-label="Close search"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Search Form */}
            <form onSubmit={handleSubmit} className="mb-8">
              <div className="relative">
                <svg
                  className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-accent-silver"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search for headsets..."
                  className="w-full pl-14 pr-6 py-5 bg-surface-coal border border-surface-slate rounded-2xl text-lg text-accent-white placeholder:text-accent-silver/50 focus:border-brand-orange focus:ring-0 transition-colors"
                />
                {isLoading && (
                  <svg className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-accent-silver animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
              </div>
            </form>

            {/* Quick Searches */}
            {!query && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="text-2xs font-mono uppercase tracking-widest text-accent-silver mb-4">
                  Popular Searches
                </h3>
                <div className="flex flex-wrap gap-2">
                  {popularSearches.map((term) => (
                    <button
                      key={term}
                      onClick={() => handleQuickSearch(term)}
                      className="px-4 py-2 bg-surface-coal border border-surface-slate rounded-xl text-sm text-accent-platinum hover:border-brand-orange hover:text-brand-orange transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Results */}
            {results && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-8"
              >
                {/* Products */}
                {results.products.length > 0 && (
                  <div>
                    <h3 className="text-2xs font-mono uppercase tracking-widest text-accent-silver mb-4">
                      Products
                    </h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {results.products.map((product) => (
                        <a
                          key={product.id}
                          href={getProductUrl(product.handle)}
                          onClick={onClose}
                          className="flex gap-4 p-3 bg-surface-coal rounded-xl border border-surface-slate hover:border-brand-orange transition-colors group"
                        >
                          <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0">
                            {product.featuredImage ? (
                              <img
                                src={product.featuredImage.url}
                                alt={product.featuredImage.altText || product.title}
                                className="w-full h-full object-contain p-1"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-surface-graphite">
                                <svg className="w-6 h-6 text-accent-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-2xs text-accent-silver font-mono">{product.vendor}</span>
                            <h4 className="text-sm font-medium text-accent-white group-hover:text-brand-orange transition-colors line-clamp-2">
                              {product.title}
                            </h4>
                            <span className="text-sm font-display font-bold text-brand-orange">
                              {formatPrice(
                                product.priceRange.minVariantPrice.amount,
                                product.priceRange.minVariantPrice.currencyCode
                              )}
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Collections */}
                {results.collections.length > 0 && (
                  <div>
                    <h3 className="text-2xs font-mono uppercase tracking-widest text-accent-silver mb-4">
                      Collections
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {results.collections.map((collection) => (
                        <a
                          key={collection.id}
                          href={getCollectionUrl(collection.handle)}
                          onClick={onClose}
                          className="px-4 py-2 bg-surface-coal border border-surface-slate rounded-xl text-sm text-accent-platinum hover:border-brand-orange hover:text-brand-orange transition-colors"
                        >
                          {collection.title}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* No Results */}
                {results.products.length === 0 && results.collections.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 bg-surface-coal rounded-2xl flex items-center justify-center">
                      <svg className="w-8 h-8 text-accent-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-accent-white mb-2">No results found</h3>
                    <p className="text-accent-silver">Try searching for a different term.</p>
                  </div>
                )}

                {/* View All Link */}
                {(results.products.length > 0 || results.collections.length > 0) && (
                  <div className="text-center">
                    <a
                      href={`/search?q=${encodeURIComponent(query)}`}
                      onClick={onClose}
                      className="btn-secondary"
                    >
                      View All Results
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </a>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
